// src/components/pages/Library/LibraryMember.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Popover,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { styled } from '@mui/material/styles';

import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronLeft,
  ChevronRight,
  InfoOutline as InfoOutlineIcon,
  PlaylistAdd as BulkAddIcon,
} from '@mui/icons-material';

import { useBranch } from './../../layouts/BranchContext';

/* =========================================================
   CUSTOM SCROLLBAR
========================================================= */

const scrollbarStyles = (theme) => {
  const dark = theme.palette.mode === 'dark';

  return {
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
      background: 'transparent',
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: dark
        ? 'rgba(255,255,255,0.25)'
        : 'rgba(0,0,0,0.20)',
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: dark
        ? 'rgba(255,255,255,0.40)'
        : 'rgba(0,0,0,0.30)',
    },

    scrollbarWidth: 'thin',

    scrollbarColor: dark
      ? 'rgba(255,255,255,0.25) transparent'
      : 'rgba(0,0,0,0.20) transparent',
  };
};

const CustomTableScrollbar = styled('div')(
  ({ theme }) => ({
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    ...scrollbarStyles(theme),
  })
);

/* =========================================================
   HELPERS
========================================================= */

const extractArray = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const getErrorMessage = (data) => {
  if (!data) {
    return 'Something went wrong';
  }

  if (typeof data === 'string') {
    return data;
  }

  if (data.detail) {
    return String(data.detail);
  }

  if (data.message) {
    if (typeof data.message === 'string') {
      return data.message;
    }

    return JSON.stringify(data.message);
  }

  if (data.errors) {
    const errors = data.errors;

    if (typeof errors === 'string') {
      return errors;
    }

    if (typeof errors === 'object') {
      const messages = [];

      Object.entries(errors).forEach(([field, value]) => {
        if (Array.isArray(value)) {
          value.forEach((message) => {
            messages.push(`${field}: ${message}`);
          });
        } else if (typeof value === 'string') {
          messages.push(`${field}: ${value}`);
        } else {
          messages.push(
            `${field}: ${JSON.stringify(value)}`
          );
        }
      });

      if (messages.length) {
        return messages.join('\n');
      }
    }
  }

  if (data.error) {
    return typeof data.error === 'string'
      ? data.error
      : JSON.stringify(data.error);
  }

  return JSON.stringify(data);
};

const formatCourseDepartment = (value) => {
  if (!value) return '';

  // Array of course objects
  if (Array.isArray(value)) {
    return value
      .map((item) => formatCourseDepartment(item))
      .filter(Boolean)
      .join(', ');
  }

  // Single course object
  if (typeof value === 'object') {
    return (
      value.course__course_name ||
      value.course_name ||
      value.department_name ||
      value.name ||
      ''
    );
  }

  return String(value);
};

const EMPTY_BULK_ROW = {
  member_type: 'student',
  admission: '',
  employee: '',
};

/* =========================================================
   COMPONENT
========================================================= */

const LibraryMember = () => {
  const theme = useTheme();

  // Global branch (navbar)
  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === 'dark';

  const textColor = isDark ? '#ffffff' : '#1f2937';

  const secondaryTextColor = isDark
    ? 'rgba(255,255,255,0.72)'
    : 'rgba(0,0,0,0.65)';

  const borderColor = isDark
    ? 'rgba(255,255,255,0.18)'
    : 'rgba(0,0,0,0.15)';

  const inputBackground = isDark
    ? 'rgba(255,255,255,0.06)'
    : '#ffffff';

  const hoverBackground = isDark
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(0,0,0,0.03)';

  /* =========================================================
     API
  ========================================================= */

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL;

  const accessToken =
    localStorage.getItem('accessToken') ||
    sessionStorage.getItem('accessToken') ||
    localStorage.getItem('access_token') ||
    sessionStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token');

  const API_TOKEN = accessToken
    ? `Bearer ${accessToken}`
    : '';

  /* =========================================================
     CURRENT USER / ORGANIZATION
  ========================================================= */

  const currentUser = useMemo(() => {
    try {
      return (
        JSON.parse(sessionStorage.getItem('user')) ||
        {}
      );
    } catch {
      return {};
    }
  }, []);

  const organizationId =
    currentUser?.organization_id ||
    currentUser?.organization?.id ||
    '';

  /* =========================================================
     STATE
  ========================================================= */

  const [members, setMembers] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const requestId = useRef(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] =
    useState('');

  // Server-side filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeFilter, setActiveFilter] =
    useState('all');
  // Draft values change inside the popover; applied filters update only on Apply.
  const [draftTypeFilter, setDraftTypeFilter] = useState('all');
  const [draftActiveFilter, setDraftActiveFilter] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filtersOpen = Boolean(filterAnchorEl);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  // Modals
  const [showAddModal, setShowAddModal] =
    useState(false);

  const [showEditModal, setShowEditModal] =
    useState(false);

  const [showDeleteModal, setShowDeleteModal] =
    useState(false);

  const [showBulkModal, setShowBulkModal] =
    useState(false);

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [selectedMember, setSelectedMember] =
    useState(null);

  const [statusMember, setStatusMember] =
    useState(null);

  const [bulkRows, setBulkRows] = useState([
    { ...EMPTY_BULK_ROW },
  ]);

  // Form
  const [memberType, setMemberType] =
    useState('student');

  const [selectedAdmission, setSelectedAdmission] =
    useState('');

  const [selectedEmployee, setSelectedEmployee] =
    useState('');

  // Snackbar
  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState('');

  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const showSnackbar = useCallback(
    (message, severity = 'success') => {
      setSnackbarMessage(
        typeof message === 'string'
          ? message
          : getErrorMessage(message)
      );

      setSnackbarSeverity(severity);
      setOpenSnackbar(true);
    },
    []
  );

  /* =========================================================
     GET MEMBERS (server-side filters)
  ========================================================= */

  const fetchMembers = useCallback(async () => {
    if (!API_BASE_URL || !API_TOKEN) {
      setMembers([]);
      return;
    }

    const currentRequest = ++requestId.current;

    try {
      setLoading(true);

      const query = new URLSearchParams();

      if (organizationId) {
        query.append('organization', organizationId);
      }

      if (selectedBranchId) {
        query.append('branch', selectedBranchId);
      }

      if (debouncedSearch) {
        query.append('search', debouncedSearch);
      }

      if (typeFilter !== 'all') {
        query.append('member_type', typeFilter);
      }

      if (activeFilter !== 'all') {
        query.append('is_active', activeFilter);
      }

      const url =
        `${API_BASE_URL}/api/library/library-member/list/` +
        (query.toString() ? `?${query.toString()}` : '');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: API_TOKEN,
        },
      });

      const data = await response.json();

      if (currentRequest !== requestId.current) {
        return;
      }

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      setMembers(extractArray(data));
    } catch (error) {
      if (currentRequest !== requestId.current) {
        return;
      }

      console.error('Library member list error:', error);

      showSnackbar(
        error.message ||
          'Failed to load library members',
        'error'
      );

      setMembers([]);
    } finally {
      if (currentRequest === requestId.current) {
        setLoading(false);
      }
    }
  }, [
    API_BASE_URL,
    API_TOKEN,
    organizationId,
    selectedBranchId,
    debouncedSearch,
    typeFilter,
    activeFilter,
    showSnackbar,
  ]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      400
    );

    return () => clearTimeout(t);
  }, [searchTerm]);

  // Back to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    typeFilter,
    activeFilter,
    rowsPerPage,
  ]);

  /* =========================================================
     GET ADMISSIONS
  ========================================================= */

  const fetchAdmissions = useCallback(async () => {
    if (!API_BASE_URL || !API_TOKEN || !organizationId) {
      setAdmissions([]);
      return;
    }

    try {
      const query = new URLSearchParams();

      query.append('organization_id', organizationId);

      if (selectedBranchId) {
        query.append('branch_id', selectedBranchId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admission/admissions/?${query.toString()}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      setAdmissions(extractArray(data));
    } catch (error) {
      console.error('Admission error:', error);
      setAdmissions([]);
    }
  }, [
    API_BASE_URL,
    API_TOKEN,
    organizationId,
    selectedBranchId,
  ]);

  /* =========================================================
     GET EMPLOYEES
  ========================================================= */

  const fetchEmployees = useCallback(async () => {
    if (!API_BASE_URL || !API_TOKEN || !organizationId) {
      setEmployees([]);
      return;
    }

    try {
      const query = new URLSearchParams();

      query.append('organization_id', organizationId);

      if (selectedBranchId) {
        query.append('branch_id', selectedBranchId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/staff/employees/filter/?${query.toString()}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      setEmployees(extractArray(data));
    } catch (error) {
      console.error('Employee error:', error);
      setEmployees([]);
    }
  }, [
    API_BASE_URL,
    API_TOKEN,
    organizationId,
    selectedBranchId,
  ]);

  // Reload dropdown options whenever the
  // navbar branch changes
  useEffect(() => {
    if (!selectedBranchId) {
      setAdmissions([]);
      setEmployees([]);
      return;
    }

    fetchAdmissions();
    fetchEmployees();
  }, [selectedBranchId, fetchAdmissions, fetchEmployees]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const totalPages = Math.max(
    1,
    Math.ceil(members.length / rowsPerPage)
  );

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return members.slice(start, start + rowsPerPage);
  }, [members, page, rowsPerPage]);

  // Step back if the last row of a page was removed
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleChangePage = (_, newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  /* =========================================================
     RESET FORM
  ========================================================= */

  const resetForm = () => {
    setMemberType('student');
    setSelectedAdmission('');
    setSelectedEmployee('');
    setSelectedMember(null);
  };

  /* =========================================================
     ADD
  ========================================================= */

  const handleAddMember = () => {
    resetForm();
    setShowAddModal(true);
  };

  /* =========================================================
     CREATE
  ========================================================= */

  const validateMemberForm = () => {
    if (!organizationId) {
      return 'Organization not found in session';
    }

    if (!selectedBranchId) {
      return 'Please select a branch from the navbar';
    }

    if (memberType === 'student' && !selectedAdmission) {
      return 'Please select a student';
    }

    if (memberType === 'staff' && !selectedEmployee) {
      return 'Please select a staff member';
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateMemberForm();

    if (validationError) {
      showSnackbar(validationError, 'warning');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        member_type: memberType,
        admission:
          memberType === 'student'
            ? Number(selectedAdmission)
            : null,
        employee:
          memberType === 'staff'
            ? Number(selectedEmployee)
            : null,
      };

      const response = await fetch(
        `${API_BASE_URL}/api/library/library-member/create/`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data =
        response.status !== 204
          ? await response.json()
          : null;

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      showSnackbar(
        data?.message ||
          'Library member created successfully',
        'success'
      );

      setShowAddModal(false);
      resetForm();

      await fetchMembers();
    } catch (error) {
      console.error('Create member error:', error);
      showSnackbar(
        error.message || 'Failed to create member',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     EDIT
  ========================================================= */

  const handleEdit = (member) => {
    setSelectedMember(member);

    const type =
      member.member_type === 'staff'
        ? 'staff'
        : 'student';

    setMemberType(type);

    setSelectedAdmission(
      type === 'student'
        ? member.admission ??
            member.admission_id ??
            ''
        : ''
    );

    setSelectedEmployee(
      type === 'staff'
        ? member.employee ??
            member.employee_id ??
            ''
        : ''
    );

    setShowEditModal(true);
  };

  /* =========================================================
     UPDATE
  ========================================================= */

  const handleUpdate = async () => {
    if (!selectedMember?.id) {
      return;
    }

    const validationError = validateMemberForm();

    if (validationError) {
      showSnackbar(validationError, 'warning');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        member_type: memberType,
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        admission:
          memberType === 'student'
            ? Number(selectedAdmission)
            : null,
        employee:
          memberType === 'staff'
            ? Number(selectedEmployee)
            : null,
      };

      const response = await fetch(
        `${API_BASE_URL}/api/library/library-member/update/${selectedMember.id}/`,
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data =
        response.status !== 204
          ? await response.json()
          : null;

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      showSnackbar(
        data?.message ||
          'Library member updated successfully',
        'success'
      );

      setShowEditModal(false);
      resetForm();

      await fetchMembers();
    } catch (error) {
      console.error('Update member error:', error);
      showSnackbar(
        error.message || 'Failed to update member',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     STATUS TOGGLE (SWITCH)
  ========================================================= */

  const handleConfirmStatus = async () => {
    if (!statusMember?.id) {
      return;
    }

    const nextActive = statusMember.is_active !== false;

    try {
      setSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/api/library/library-member/status/${statusMember.id}/`,
        {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            is_active: !nextActive,
          }),
        }
      );

      const data =
        response.status !== 204
          ? await response.json()
          : null;

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      showSnackbar(
        data?.message ||
          `Member ${!nextActive ? 'activated' : 'deactivated'} successfully`,
        'success'
      );

      setStatusMember(null);

      await fetchMembers();
    } catch (error) {
      console.error('Status member error:', error);
      showSnackbar(
        error.message || 'Failed to update status',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DELETE (SOFT DELETE)
  ========================================================= */

  const handleShowDeleteModal = (member) => {
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedMember(null);
  };

  const handleDelete = async () => {
    if (!selectedMember?.id) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/api/library/library-member/delete/${selectedMember.id}/`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
            'Content-Type': 'application/json',
          },
        }
      );

      const data =
        response.status !== 204
          ? await response.json()
          : null;

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      showSnackbar(
        data?.message ||
          'Library member deleted successfully',
        'success'
      );

      handleCloseDeleteModal();

      await fetchMembers();
    } catch (error) {
      console.error('Delete member error:', error);
      showSnackbar(
        error.message || 'Failed to delete member',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     BULK ADD
  ========================================================= */

  const openBulkModal = () => {
    setBulkRows([{ ...EMPTY_BULK_ROW }]);
    setShowBulkModal(true);
  };

  const updateBulkRow = (i, field, value) =>
    setBulkRows((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, [field]: value } : r
      )
    );

  const addBulkRow = () =>
    setBulkRows((rows) => [...rows, { ...EMPTY_BULK_ROW }]);

  const removeBulkRow = (i) =>
    setBulkRows((rows) =>
      rows.length === 1
        ? rows
        : rows.filter((_, idx) => idx !== i)
    );

  const handleBulkSave = async () => {
    if (!organizationId) {
      showSnackbar(
        'Organization not found in session',
        'error'
      );
      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        'Please select a branch from the navbar',
        'error'
      );
      return;
    }

    for (let i = 0; i < bulkRows.length; i++) {
      const r = bulkRows[i];
      const n = i + 1;

      if (r.member_type === 'student' && !r.admission) {
        showSnackbar(
          `Member ${n}: please select a student`,
          'error'
        );
        return;
      }

      if (r.member_type === 'staff' && !r.employee) {
        showSnackbar(
          `Member ${n}: please select a staff member`,
          'error'
        );
        return;
      }
    }

    // No duplicates inside one request
    const keys = bulkRows.map((r) =>
      r.member_type === 'student'
        ? `student|${r.admission}`
        : `staff|${r.employee}`
    );

    if (new Set(keys).size !== keys.length) {
      showSnackbar(
        'Duplicate members found in the list',
        'error'
      );
      return;
    }

    const payload = {
      organization: Number(organizationId),
      branch: Number(selectedBranchId),
      members: bulkRows.map((r) => ({
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        member_type: r.member_type,
        admission:
          r.member_type === 'student'
            ? Number(r.admission)
            : null,
        employee:
          r.member_type === 'staff'
            ? Number(r.employee)
            : null,
      })),
    };

    try {
      setSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/api/library/library-member/bulk-create/`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: API_TOKEN,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data =
        response.status !== 204
          ? await response.json()
          : null;

      console.log('BULK CREATE MEMBER RESPONSE:', data);

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      showSnackbar(
        data?.message ||
          'Library members created successfully',
        'success'
      );

      setShowBulkModal(false);

      await fetchMembers();
    } catch (error) {
      console.error('Bulk create member error:', error);
      showSnackbar(
        error.message ||
          'Failed to bulk create members',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DISPLAY HELPERS
  ========================================================= */

  const toText = (v) =>
  v === null || v === undefined || typeof v === 'object' ? '' : String(v);

const getMemberName = (member) =>
  toText(member.member_name) ||
  toText(member.student_name) ||
  toText(member.admission_name) ||
  toText(member.employee_name) ||
  toText(member.name) ||
  '—';

const getMemberCode = (member) =>
  toText(member.member_code) ||
  toText(member.admission_no) ||
  toText(member.employee_code) ||
  '—';

  const getMemberType = (member) => {
    if (member.member_type === 'staff') {
      return 'Staff';
    }

    if (member.member_type === 'student') {
      return 'Student';
    }

    return '—';
  };

  /* =========================================================
     SHARED INPUT STYLES
  ========================================================= */

  const selectSx = {
    color: textColor,
    backgroundColor: inputBackground,

    '& .MuiSelect-select': {
      color: textColor,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: borderColor,
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },

    '& .MuiSvgIcon-root': {
      color: textColor,
    },
  };

  const inputLabelSx = {
    color: secondaryTextColor,

    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor:
          theme.palette.background.paper,
        color: textColor,
        ...scrollbarStyles(theme),
        '& .MuiMenuItem-root': {
          color: textColor,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor:
            theme.palette.action.hover,
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor:
            theme.palette.action.selected,
        },
      },
    },
  };

  const pillSelectSx = {
    height: 50,
    minWidth: 180,
    borderRadius: '30px',
    color: textColor,
    backgroundColor: inputBackground,

    '& .MuiSelect-select': {
      color: textColor,
      paddingLeft: '20px',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: borderColor,
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },

    '& .MuiSvgIcon-root': {
      color: textColor,
    },
  };

  const renderFilter = (label, value, onChange, options) => (
    <FormControl size="small">
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) =>
          `${label}: ${options.find(
            (o) => String(o.value) === String(v)
          )?.label ?? 'All'}`
        }
        inputProps={{ 'aria-label': label }}
        sx={pillSelectSx}
        MenuProps={menuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <Container maxWidth={false} sx={{ color: textColor }}>
      {/* =====================================================
          SEARCH + FILTERS + ADD
      ===================================================== */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
          }}
        >
          {/* SEARCH */}

          <TextField
            variant="outlined"
            placeholder="Search by Admission No, Employee Code or Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      ml: 1,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                  >
                    <CloseIcon
                      fontSize="small"
                      sx={{ color: secondaryTextColor }}
                    />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 350,
              maxWidth: '100%',

              '& .MuiOutlinedInput-root': {
                height: 50,
                borderRadius: '30px',
                backgroundColor: inputBackground,

                '& fieldset': {
                  borderColor: borderColor,
                },

                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },

                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },

              '& .MuiInputBase-input': {
                color: textColor,
              },

              '& .MuiInputBase-input::placeholder': {
                color: secondaryTextColor,
                opacity: 1,
              },
            }}
          />

          {/* FILTER BUTTON - existing filters moved here */}
          <Button
            variant="contained"
            onClick={(event) => {
              // Start each filter session from the currently applied values.
              setDraftTypeFilter(typeFilter);
              setDraftActiveFilter(activeFilter);
              setFilterAnchorEl(event.currentTarget);
            }}
            startIcon={<FilterListIcon />}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: '10px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              borderColor: borderColor,
             color: theme.palette.primary.disabled,
            }}
          >
            Filters{(typeFilter !== 'all' ? 1 : 0) + (activeFilter !== 'all' ? 1 : 0) > 0
              ? ` (${(typeFilter !== 'all' ? 1 : 0) + (activeFilter !== 'all' ? 1 : 0)})`
              : ''}
          </Button>

          <Popover
            open={filtersOpen}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 2,
                width: { xs: 'calc(100vw - 32px)', sm: 320 },
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                color: textColor,
                border: `1px solid ${borderColor}`,
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                Filter Members
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {renderFilter(
                  'Type',
                  draftTypeFilter,
                  setDraftTypeFilter,
                  [
                    { value: 'all', label: 'All' },
                    { value: 'student', label: 'Student' },
                    { value: 'staff', label: 'Staff' },
                  ]
                )}
                {renderFilter(
                  'Status',
                  draftActiveFilter,
                  setDraftActiveFilter,
                  [
                    { value: 'all', label: 'All' },
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' },
                  ]
                )}
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={() => {
                    setDraftTypeFilter('all');
                    setDraftActiveFilter('all');
                  }}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setTypeFilter(draftTypeFilter);
                    setActiveFilter(draftActiveFilter);
                    setPage(1);
                    setFilterAnchorEl(null);
                  }}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Popover>

          {/* ADD */}

          <Button
            variant="contained"
            onClick={handleAddMember}
            startIcon={<AddIcon />}
            sx={{
              height: 50,
              px: 3,
              borderRadius: '10px',
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            ADD MEMBER
          </Button>

          {/* BULK ADD */}

          <Button
            variant="contained"
            onClick={openBulkModal}
            startIcon={<BulkAddIcon />}
            sx={{
              height: 50,
              px: 3,
              borderRadius: '10px',
              fontWeight: 700,
            }}
          >
            BULK ADD
          </Button>
        </Box>

        {/* GUIDE */}

        <Tooltip title="User Guide">
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 50,
              height: 50,
              color: theme.palette.primary.main,
            }}
          >
            <InfoOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Paper
        elevation={3}
        sx={{
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          color: textColor,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <TableContainer component={CustomTableScrollbar}>
          <Table
            sx={{
              minWidth: 950,

              '& .MuiTableCell-root': {
                color: textColor,
                borderColor: borderColor,
              },

              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: '#ffffff',
                fontSize: '0.95rem',
              },

              '& .MuiTableBody-root .MuiTableRow-root:hover': {
                backgroundColor: hoverBackground,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Member Name</TableCell>
                <TableCell>Member Type</TableCell>
                <TableCell>Member Code</TableCell>
                <TableCell>Course / Designation</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ color: secondaryTextColor }}>
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((member, index) => (
                  <TableRow key={member.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          color: textColor,
                          fontWeight: 600,
                        }}
                      >
                        {getMemberName(member)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={getMemberType(member)}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          backgroundColor:
                            member.member_type === 'staff'
                              ? theme.palette.warning.main
                              : theme.palette.info.main,
                          color: '#ffffff',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {getMemberCode(member)}
                    </TableCell>

                    <TableCell>
  {formatCourseDepartment(member.course_department) ||
    formatCourseDepartment(member.designation_name) ||
    '—'}
</TableCell>

                    <TableCell>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Tooltip
                          title={
                            member.is_active === false
                              ? 'Click to activate'
                              : 'Click to deactivate'
                          }
                        >
                          <span>
                            <Switch
                              size="small"
                              checked={
                                member.is_active !== false
                              }
                              onChange={() =>
                                setStatusMember(member)
                              }
                            />
                          </span>
                        </Tooltip>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color:
                              member.is_active === false
                                ? theme.palette.error.main
                                : theme.palette.success.main,
                          }}
                        >
                          {member.is_active === false
                            ? 'Inactive'
                            : 'Active'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="center">
                      {/* EDIT */}

                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleEdit(member)}
                          sx={{
                            color: theme.palette.primary.main,
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      {/* DELETE */}

                      {member.is_active !== false && (
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleShowDeleteModal(member)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ color: secondaryTextColor }}>
                      No Library Members Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* =====================================================
          PAGINATION
      ===================================================== */}

      {members.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: secondaryTextColor }}>
            Page{' '}
            <strong style={{ color: textColor }}>{page}</strong> of{' '}
            <strong style={{ color: textColor }}>{totalPages}</strong>
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{ color: theme.palette.primary.main }}
            >
              <ChevronLeft />
            </IconButton>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((number) => (
              <IconButton
                key={number}
                onClick={() => handleChangePage(null, number)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',

                  backgroundColor:
                    page === number
                      ? theme.palette.primary.main
                      : 'transparent',

                  color:
                    page === number ? '#ffffff' : textColor,

                  '&:hover': {
                    backgroundColor:
                      page === number
                        ? theme.palette.primary.dark
                        : hoverBackground,
                  },
                }}
              >
                {number}
              </IconButton>
            ))}

            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= totalPages}
              sx={{ color: theme.palette.primary.main }}
            >
              <ChevronRight />
            </IconButton>

            <FormControl
              size="small"
              sx={{
                minWidth: 90,
                ml: 1,

                '& .MuiInputLabel-root': inputLabelSx,
              }}
            >
              <InputLabel>Rows</InputLabel>

              <Select
                value={rowsPerPage}
                label="Rows"
                onChange={handleChangeRowsPerPage}
                sx={{ ...selectSx, height: 36 }}
                MenuProps={menuProps}
              >
                {[5, 10, 25, 50].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* =====================================================
          ADD / EDIT MODAL
      ===================================================== */}

      <Modal
        open={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            maxWidth: '95%',
            bgcolor: theme.palette.background.paper,
            color: textColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,

              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: '#ffffff', fontWeight: 700 }}
            >
              {showEditModal
                ? 'Edit Library Member'
                : 'Add Library Member'}
            </Typography>

            <IconButton
              onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              sx={{ color: '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* FORM */}

          <Box
            sx={{
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {/* MEMBER TYPE */}

            <FormControl
              fullWidth
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': inputLabelSx,
              }}
            >
              <InputLabel>Member Type</InputLabel>

              <Select
                value={memberType}
                label="Member Type"
                onChange={(e) => {
                  setMemberType(e.target.value);
                  setSelectedAdmission('');
                  setSelectedEmployee('');
                }}
                sx={selectSx}
                MenuProps={menuProps}
              >
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
              </Select>
            </FormControl>

            {/* STUDENT */}

            {memberType === 'student' && (
              <Autocomplete
                fullWidth
                options={admissions}
                value={
                  admissions.find(
                    (student) =>
                      String(student.id) ===
                      String(selectedAdmission)
                  ) || null
                }
                onChange={(_, student) =>
                  setSelectedAdmission(student?.id ?? '')
                }
                getOptionLabel={(student) =>
                  String(
                    student.student_name ||
                      student.candidate_name ||
                      student.admission_no ||
                      student.name ||
                      `Student ${student.id}`
                  )
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                noOptionsText="No students found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Student"
                    placeholder="Search students..."
                    sx={{
                      mb: 2,
                      '& .MuiInputLabel-root': inputLabelSx,
                    }}
                  />
                )}
                sx={selectSx}
              />
            )}

            {/* STAFF */}

            {memberType === 'staff' && (
              <Autocomplete
                fullWidth
                options={employees}
                value={
                  employees.find(
                    (employee) =>
                      String(employee.id) ===
                      String(selectedEmployee)
                  ) || null
                }
                onChange={(_, employee) =>
                  setSelectedEmployee(employee?.id ?? '')
                }
                getOptionLabel={(employee) =>
                  String(
                    employee.name ||
                      employee.employee_name ||
                      employee.employee_code ||
                      `Staff ${employee.id}`
                  )
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                noOptionsText="No staff found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Staff"
                    placeholder="Search staff..."
                    sx={{
                      mb: 2,
                      '& .MuiInputLabel-root': inputLabelSx,
                    }}
                  />
                )}
                sx={selectSx}
              />
            )}

            {/* BUTTONS */}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                disabled={saving}
                sx={{
                  color: textColor,
                  borderColor: borderColor,

                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                onClick={
                  showEditModal ? handleUpdate : handleSave
                }
                disabled={
                  saving ||
                  !selectedBranchId ||
                  (memberType === 'student' && !selectedAdmission) ||
                  (memberType === 'staff' && !selectedEmployee)
                }
                sx={{ fontWeight: 700 }}
              >
                {saving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : showEditModal ? (
                  'UPDATE'
                ) : (
                  'SAVE'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          STATUS MODAL
      ===================================================== */}

      <Modal
        open={Boolean(statusMember)}
        onClose={() => !saving && setStatusMember(null)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxWidth: '90%',
            bgcolor: theme.palette.background.paper,
            color: textColor,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor:
                statusMember?.is_active === false
                  ? theme.palette.success.main
                  : theme.palette.error.main,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: '#fff', fontWeight: 700 }}
            >
              {statusMember?.is_active === false
                ? 'Activate'
                : 'Deactivate'}{' '}
              Confirmation
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography>
              Are you sure you want to{' '}
              {statusMember?.is_active === false
                ? 'activate'
                : 'deactivate'}{' '}
              <strong>
                {statusMember
                  ? getMemberName(statusMember)
                  : ''}
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={() => setStatusMember(null)}
                disabled={saving}
                sx={{
                  color: textColor,
                  borderColor: borderColor,
                }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color={
                  statusMember?.is_active === false
                    ? 'success'
                    : 'error'
                }
                onClick={handleConfirmStatus}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'YES, CONFIRM'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          BULK ADD MODAL
      ===================================================== */}

      <Modal
        open={showBulkModal}
        onClose={() => !saving && setShowBulkModal(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxWidth: '95%',
            maxHeight: '90vh',
            bgcolor: theme.palette.background.paper,
            color: textColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* HEADER (fixed) */}

          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: '#ffffff', fontWeight: 700 }}
            >
              Bulk Add Library Members
            </Typography>

            <IconButton
              onClick={() => !saving && setShowBulkModal(false)}
              sx={{ color: '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* BODY (scrollable) */}

          <CustomTableScrollbar
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '24px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 2, color: secondaryTextColor }}
            >
              Duplicate members inside one request are not allowed.
              Already-active members are skipped, and inactive
              members are restored automatically.
            </Typography>

            {bulkRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    Member {index + 1}
                  </Typography>

                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeBulkRow(index)}
                    disabled={bulkRows.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                    },
                    gap: 2,
                  }}
                >
                  {/* MEMBER TYPE */}

                  <FormControl fullWidth>
                    <InputLabel sx={inputLabelSx}>
                      Member Type
                    </InputLabel>

                    <Select
                      value={row.member_type}
                      label="Member Type"
                      onChange={(e) =>
                        updateBulkRow(
                          index,
                          'member_type',
                          e.target.value
                        )
                      }
                      sx={selectSx}
                      MenuProps={menuProps}
                    >
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="staff">Staff</MenuItem>
                    </Select>
                  </FormControl>

                  {/* STUDENT / STAFF */}

                  {row.member_type === 'student' ? (
                    <Autocomplete
                      fullWidth
                      options={admissions}
                      value={
                        admissions.find(
                          (student) =>
                            String(student.id) ===
                            String(row.admission)
                        ) || null
                      }
                      onChange={(_, student) =>
                        updateBulkRow(
                          index,
                          'admission',
                          student?.id ?? ''
                        )
                      }
                      getOptionLabel={(student) =>
                        String(
                          student.student_name ||
                            student.candidate_name ||
                            student.admission_no ||
                            student.name ||
                            `Student ${student.id}`
                        )
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      noOptionsText="No students found"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Student"
                          placeholder="Search students..."
                          sx={{
                            '& .MuiInputLabel-root': inputLabelSx,
                          }}
                        />
                      )}
                      sx={selectSx}
                    />
                  ) : (
                    <Autocomplete
                      fullWidth
                      options={employees}
                      value={
                        employees.find(
                          (employee) =>
                            String(employee.id) ===
                            String(row.employee)
                        ) || null
                      }
                      onChange={(_, employee) =>
                        updateBulkRow(
                          index,
                          'employee',
                          employee?.id ?? ''
                        )
                      }
                      getOptionLabel={(employee) =>
                        String(
                          employee.name ||
                            employee.employee_name ||
                            employee.employee_code ||
                            `Staff ${employee.id}`
                        )
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      noOptionsText="No staff found"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Staff"
                          placeholder="Search staff..."
                          sx={{
                            '& .MuiInputLabel-root': inputLabelSx,
                          }}
                        />
                      )}
                      sx={selectSx}
                    />
                  )}
                </Box>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addBulkRow}
              sx={{ color: textColor }}
            >
              Add another member
            </Button>
          </CustomTableScrollbar>

          {/* FOOTER (fixed) */}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 2,
              flexShrink: 0,
              borderTop: `1px solid ${borderColor}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Button
              variant="contained"
              onClick={handleBulkSave}
              disabled={saving || !selectedBranchId}
              sx={{ fontWeight: 700 }}
            >
              {saving ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'SAVE ALL'
              )}
            </Button>

            <Button
              variant="contained"
              onClick={() => setShowBulkModal(false)}
              disabled={saving}
              sx={{
                color: textColor,
                borderColor: borderColor,
              }}
            >
              CANCEL
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          DELETE MODAL
      ===================================================== */}

      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxWidth: '90%',
            bgcolor: theme.palette.background.paper,
            color: textColor,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.error.main,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: '#ffffff', fontWeight: 700 }}
            >
              Delete Confirmation
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: textColor }}>
              Are you sure you want to delete{' '}
              <strong>
                {selectedMember
                  ? getMemberName(selectedMember)
                  : ''}
              </strong>
              ?
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 1, color: secondaryTextColor }}
            >
              This is a soft delete. If the same student or staff
              is added again later, this record will be restored
              instead of creating a new one.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseDeleteModal}
                disabled={saving}
                sx={{
                  color: textColor,
                  borderColor: borderColor,
                }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'YES, DELETE'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          USER GUIDE
      ===================================================== */}

      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 850,
            maxWidth: '95%',
            maxHeight: '90vh',
            bgcolor: theme.palette.background.paper,
            color: textColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: '#ffffff', fontWeight: 700 }}
            >
              Library Member - User Guide
            </Typography>

            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{ maxHeight: 'calc(90vh - 72px)', p: 3 }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Add a Library Member
            </Typography>

            <Box
              component="ul"
              sx={{ color: textColor, lineHeight: 1.8 }}
            >
              <li>
                Click <strong>ADD MEMBER</strong>.
              </li>
              <li>Select Student or Staff.</li>
              <li>Select the student or staff member.</li>
              <li>
                Click <strong>SAVE</strong>.
              </li>
              <li>
                If the same inactive member already exists, the
                API restores it instead of creating a new record.
              </li>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Bulk Add Library Members
            </Typography>

            <Box
              component="ul"
              sx={{ color: textColor, lineHeight: 1.8 }}
            >
              <li>
                Click <strong>BULK ADD</strong>.
              </li>
              <li>
                Add multiple members. Choose Student or Staff
                and pick the person for each row.
              </li>
              <li>
                Duplicate members inside one request are not
                allowed.
              </li>
              <li>
                Click <strong>SAVE ALL</strong>.
              </li>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Branch Selection
            </Typography>

            <Typography sx={{ color: textColor }}>
              Branch is selected globally from the navbar. The
              member list, student and staff dropdowns all
              follow that selection automatically.
            </Typography>

            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Edit a Library Member
            </Typography>

            <Box
              component="ul"
              sx={{ color: textColor, lineHeight: 1.8 }}
            >
              <li>Click the Edit icon.</li>
              <li>Change the member type or person.</li>
              <li>
                Click <strong>UPDATE</strong>.
              </li>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Activate / Deactivate a Member
            </Typography>

            <Box
              component="ul"
              sx={{ color: textColor, lineHeight: 1.8 }}
            >
              <li>Use the Switch in the Status column.</li>
              <li>Confirmation modal will appear.</li>
              <li>
                Click <strong>YES, CONFIRM</strong>.
              </li>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Delete a Member
            </Typography>

            <Box
              component="ul"
              sx={{ color: textColor, lineHeight: 1.8 }}
            >
              <li>Click the Delete (trash) icon.</li>
              <li>
                This performs a soft delete via the API.
              </li>
              <li>
                Confirm with <strong>YES, DELETE</strong>.
              </li>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Search & Filters
            </Typography>

            <Box
              component="ul"
              sx={{ color: textColor, lineHeight: 1.8 }}
            >
              <li>
                Search by Admission No, Employee Code or Name.
                Search is sent to the API.
              </li>
              <li>
                Filter by Type (Student / Staff) and Status
                (Active / Inactive).
              </li>
            </Box>
          </CustomTableScrollbar>
        </Box>
      </Modal>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setOpenSnackbar(false)}
          sx={{
            width: '100%',
            maxWidth: 600,
            whiteSpace: 'pre-line',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LibraryMember;